import React from 'react';

/**
 * Read-only Babylon position "decision card" surfaced inline in the public
 * /coach chat when the coach emits a roll/hold/close decision link and the
 * reader taps it. The third artifact type alongside PublicStockCard and
 * PublicContractCard — same dark-ledger aesthetic (mono numerals, blue spine).
 *
 * The centerpiece is the THREE intents (Keep My Shares / Cycle for Income /
 * Harvest Premium), each with the engine's verbatim reasoning and a traffic-
 * light decision badge. Honest framing only — the card surfaces what the
 * user's own rules say, never "we recommend". Presentation only, no CTA
 * (a later phase). All fields are nullable (API-only data policy) → "—".
 */

type TrafficLight = 'green' | 'yellow' | 'red';

interface FlipCondition {
  summary: string | null;
  would_become: string | null;
}

export interface PositionIntent {
  decision: string | null; // HOLD / CLOSE / ROLL / LET ASSIGN / HARVEST, or null
  action: string | null;
  traffic_light: TrafficLight | null;
  reasoning: string | null; // verbatim from the engine
  flip_condition: FlipCondition | null;
  roll_target: string | null;
  expected_loss: number | null;
  note?: string | null; // shown when decision couldn't compute
}

export interface RollCandidate {
  label: string | null;
  strike: number | null;
  expiration_date: string | null;
  days_to_expiration: number | null;
  delta: number | null;
  net_roll_credit: number | null;
  new_safety_floor: number | null;
  cushion_pct: number | null;
  annualized_return_pct: number | null;
  open_interest: number | null;
  liquidity_level: string | null;
}

export interface PublicPositionCardData {
  position: {
    symbol: string;
    position_type: 'call' | 'put' | string;
    strike: number | null;
    expiry: string | null;
    cost_basis: number | null;
    num_contracts: number | null;
    current_price: number | null;
    current_premium: number | null;
    premium_captured_pct: number | null;
    days_to_expiration: number | null;
    moneyness: string | null;
    is_itm: boolean | null;
    pct_otm: number | null;
    intrinsic_per_share: number | null;
    safety_floor: number | null;
    simple_pnl: number | null;
    delta: number | null;
    implied_volatility: number | null; // 0–1 fraction
  };
  entry_premium: number | null;
  entry_premium_estimated: boolean;
  intents: {
    hold: PositionIntent;
    wheel: PositionIntent;
    release: PositionIntent;
  };
  roll_candidates: RollCandidate[];
  notes: string[];
}

const DASH = '—';

const fmtMoney = (v: number | null | undefined): string =>
  v == null
    ? DASH
    : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMoneySigned = (v: number | null | undefined): string => {
  if (v == null) return DASH;
  const s = `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return v >= 0 ? `+${s}` : `−${s}`;
};

const fmtPct = (v: number | null | undefined, sign = false): string => {
  if (v == null) return DASH;
  const s = `${Math.abs(v) < 100 ? v.toFixed(1) : v.toFixed(0)}%`;
  return sign && v >= 0 ? `+${s}` : s;
};

const fmtDelta = (v: number | null | undefined): string =>
  v == null ? DASH : v.toFixed(2);

const fmtNum = (v: number | null | undefined): string =>
  v == null ? DASH : v.toLocaleString();

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Intent key → human label (the user-facing Discipline framing).
 * The conservative 'hold' label is position-type-aware: a CSP holds no shares,
 * so its conservative stance is to stay in cash rather than keep shares.
 */
const intentOrder = (isCsp: boolean): Array<{ key: 'hold' | 'wheel' | 'release'; label: string }> => [
  { key: 'hold', label: isCsp ? "If you'd rather stay in cash" : 'If you want to keep your shares' },
  { key: 'wheel', label: 'If you want to cycle for income' },
  { key: 'release', label: 'If you want to harvest premium' },
];

/** A labelled position fact in the "Your position" strip. */
const Fact: React.FC<{ label: string; value: React.ReactNode; cls?: string }> = ({
  label,
  value,
  cls,
}) => (
  <div className="ppc-fact">
    <span className="ppc-fact-label">{label}</span>
    <span className={`ppc-fact-val${cls ? ` ${cls}` : ''}`}>{value}</span>
  </div>
);

/** One of the three intent decision blocks (the centerpiece). */
const IntentBlock: React.FC<{ label: string; intent: PositionIntent }> = ({
  label,
  intent,
}) => {
  const hasDecision = intent.decision != null;
  const light = intent.traffic_light;
  const flip = intent.flip_condition?.summary;
  // The engine's reasoning often already ends with the flip sentence; strip it
  // so we don't render "Flips back to Hold…" twice (reason line + flip sub-line).
  const reason =
    hasDecision && flip && intent.reasoning?.includes(flip)
      ? intent.reasoning.replace(flip, '').trim()
      : intent.reasoning;

  return (
    <div className="ppc-intent">
      <div className="ppc-intent-head">
        <span className="ppc-intent-label">{label}</span>
        {hasDecision ? (
          <span
            className={`ppc-decision${light ? ` ppc-decision--${light}` : ''}`}
          >
            {intent.decision}
          </span>
        ) : (
          <span className="ppc-decision ppc-decision--null">{DASH}</span>
        )}
      </div>
      {hasDecision ? (
        <p className="ppc-intent-reason">{reason}</p>
      ) : (
        <p className="ppc-intent-reason ppc-intent-reason--muted">
          {intent.note || "Couldn't compute this one."}
        </p>
      )}
      {hasDecision && flip && (
        <p className="ppc-intent-flip">{flip}</p>
      )}
    </div>
  );
};

/** A single roll candidate row — the real roll math, shown cleanly. */
const RollRow: React.FC<{ cand: RollCandidate }> = ({ cand }) => (
  <div className="ppc-roll">
    <div className="ppc-roll-head">
      <span className="ppc-roll-label">{cand.label || DASH}</span>
      <span
        className={`ppc-roll-credit${
          cand.net_roll_credit != null && cand.net_roll_credit < 0
            ? ' ppc-roll-credit--debit'
            : ''
        }`}
      >
        {fmtMoneySigned(cand.net_roll_credit)}
        {cand.net_roll_credit != null && (
          <span className="ppc-roll-credit-tag">
            {cand.net_roll_credit >= 0 ? ' net credit' : ' net debit'}
          </span>
        )}
      </span>
    </div>
    <div className="ppc-roll-stats">
      <span className="ppc-roll-stat">
        <span className="ppc-roll-stat-k">Floor</span>
        {fmtMoney(cand.new_safety_floor)}
      </span>
      <span className="ppc-roll-stat">
        <span className="ppc-roll-stat-k">Cushion</span>
        {fmtPct(cand.cushion_pct)}
      </span>
      <span className="ppc-roll-stat">
        <span className="ppc-roll-stat-k">Ann.</span>
        {fmtPct(cand.annualized_return_pct)}
      </span>
      <span className="ppc-roll-stat">
        <span className="ppc-roll-stat-k">Liq.</span>
        {cand.liquidity_level || (cand.open_interest != null ? `${fmtNum(cand.open_interest)} OI` : DASH)}
      </span>
    </div>
  </div>
);

const PublicPositionCard: React.FC<{ data: PublicPositionCardData }> = ({ data }) => {
  const p = data.position;
  // position_type may arrive as 'call'/'put' (coach normalized) or
  // 'covered_call'/'cash_secured_put' (raw) — match on "put" either way.
  const isCsp = (p.position_type || '').toLowerCase().includes('put');
  const isCall = !isCsp;
  const isItm = p.is_itm === true;

  // When all three goals land on the SAME decision + reasoning, collapse to one
  // unified verdict — three identical blocks under "depends on your goal" reads
  // as a bug. The card fans out into three only when the intents genuinely
  // diverge, which is exactly when that divergence is the whole point.
  const intentList = [data.intents.hold, data.intents.wheel, data.intents.release];
  const allAgree =
    intentList.every((i) => i && i.decision != null) &&
    intentList.every((i) => i.decision === intentList[0].decision) &&
    intentList.every((i) => i.reasoning === intentList[0].reasoning);

  const pnl = p.simple_pnl;
  const pnlClass = pnl == null ? '' : pnl >= 0 ? 'ppc-fact-val--green' : 'ppc-fact-val--red';

  const captured = p.premium_captured_pct;
  const capturedPct =
    captured == null ? 0 : Math.max(0, Math.min(100, captured));

  // The moneyness read for the header: ITM shows ITM%, else OTM%.
  const moneyLabel = isItm ? 'ITM' : 'OTM';
  const moneyVal = isItm
    ? // when ITM, pct_otm is negative / null — show the absolute distance if present
      (p.pct_otm != null ? fmtPct(Math.abs(p.pct_otm)) : DASH)
    : fmtPct(p.pct_otm);

  const topRolls = (data.roll_candidates || []).slice(0, 2);

  return (
    <div
      className="ppc-card"
      role="group"
      aria-label={`${p.symbol} position decision card`}
    >
      {/* Header — symbol + strike/type badge */}
      <div className="ppc-head">
        <div className="ppc-id">
          <span className="ppc-symbol">{p.symbol}</span>
          <span className="ppc-strike">{fmtMoney(p.strike)}</span>
          <span
            className={`ppc-badge${isCall ? ' ppc-badge--call' : ' ppc-badge--put'}`}
          >
            {isCall ? 'CALL' : 'PUT'}
          </span>
        </div>
      </div>

      {/* Sub — expiry · DTE · moneyness · stock price */}
      <div className="ppc-sub">
        <span className="ppc-sub-item">{fmtDate(p.expiry)}</span>
        <span className="ppc-sub-dot" aria-hidden="true">·</span>
        <span className="ppc-sub-item">
          {p.days_to_expiration == null ? DASH : `${p.days_to_expiration}d`}
        </span>
        <span className="ppc-sub-dot" aria-hidden="true">·</span>
        <span className="ppc-sub-item">
          {moneyVal} {moneyLabel}
        </span>
        <span className="ppc-sub-dot" aria-hidden="true">·</span>
        <span className="ppc-sub-item">stock {fmtMoney(p.current_price)}</span>
      </div>

      {/* Your position strip */}
      <div className="ppc-position">
        <div className="ppc-facts">
          {/* A cash-secured put has no share cost basis — the value is the
              strike, i.e. the price you'd be obligated to buy at if assigned.
              Matches the app-wide CSP convention (see ResolveModal). */}
          <Fact
            label={isCsp ? 'Basis if assigned' : 'Cost basis'}
            value={fmtMoney(p.cost_basis)}
          />
          <Fact
            label="Entry premium"
            value={
              data.entry_premium == null ? (
                DASH
              ) : data.entry_premium_estimated ? (
                <>
                  ≈ {fmtMoney(data.entry_premium)}{' '}
                  <span className="ppc-est">(estimated)</span>
                </>
              ) : (
                fmtMoney(data.entry_premium)
              )
            }
          />
          <Fact label="Safety floor" value={fmtMoney(p.safety_floor)} />
          <Fact label="P&L" value={fmtMoneySigned(pnl)} cls={pnlClass} />
          <Fact label="Delta" value={fmtDelta(p.delta)} />
          <Fact
            label="Contracts"
            value={p.num_contracts == null ? DASH : fmtNum(p.num_contracts)}
          />
        </div>
        {/* Premium captured bar */}
        <div className="ppc-captured">
          <div className="ppc-captured-head">
            <span className="ppc-captured-label">Premium captured</span>
            <span className="ppc-captured-val">{fmtPct(captured)}</span>
          </div>
          <div className="ppc-captured-track">
            <div
              className="ppc-captured-fill"
              style={{ width: `${capturedPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* The decision — the three intents (centerpiece) */}
      <div className="ppc-decisions">
        <div className="ppc-decisions-title">
          {allAgree ? 'Same call — whatever your goal' : 'Your move depends on your goal'}
        </div>
        {allAgree ? (
          <IntentBlock label="Every goal points the same way" intent={data.intents.hold} />
        ) : (
          intentOrder(isCsp).map(({ key, label }) => (
            <IntentBlock key={key} label={label} intent={data.intents[key]} />
          ))
        )}
      </div>

      {/* Roll candidates — the real roll math (top 1–2) */}
      {topRolls.length > 0 && (
        <div className="ppc-rolls">
          <div className="ppc-rolls-title">If you roll, your best options</div>
          {topRolls.map((c, i) => (
            <RollRow key={`${c.label ?? 'roll'}-${i}`} cand={c} />
          ))}
        </div>
      )}

      <div className="ppc-foot">
        <span className="ppc-foot-rules">
          Your rules, with default thresholds. Not advice.
        </span>
        <span className="ppc-foot-brand">via Babylon</span>
      </div>
    </div>
  );
};

/** Slim skeleton shown while a position card is fetching. */
export const PublicPositionCardSkeleton: React.FC = () => (
  <div className="ppc-card ppc-card--skel" aria-hidden="true">
    <div className="psc-skel-line psc-skel-line--head" />
    <div className="psc-skel-line psc-skel-line--price" />
    <div className="psc-skel-line psc-skel-line--bar" />
    <div className="psc-skel-line psc-skel-line--bar" />
    <div className="psc-skel-line psc-skel-line--bar" />
  </div>
);

/** Quiet inline notice when a position card can't load (non-404 error). */
export const PublicPositionCardError: React.FC = () => (
  <div className="ppc-card ppc-card--error">
    Couldn't load that position right now.
  </div>
);

export default PublicPositionCard;
